/**
 * Générer un dashboard sur mesure en conversant avec Claude.
 *
 * Conversation à gauche, aperçu réel à droite. On décrit, on voit écrire, on prévisualise avec de
 * vraies données, on garde.
 *
 * ## ⚠️ Le piège de l'iframe, dans sa variante la plus traître
 *
 * L'aperçu se **démonte**, il ne se masque pas. Une iframe posée dans un `display: none` est
 * exactement la frame sans vue déjà documentée dans `DashboardSurMesureView.jsx` : Chrome ne lui
 * attribue jamais de vue, `innerWidth` et `innerHeight` restent à zéro, rien ne se met en page —
 * et **tout le reste marche**. Le préambule répond, les données arrivent, la hauteur ne vient
 * jamais. On cherche alors un défaut de données pendant une heure.
 *
 * C'est pour la même raison que la bascule entre deux versions passe par une clé de montage :
 * `versionId` est dans les dépendances de l'hôte, mais une iframe dont on change le `srcdoc` à
 * chaud garde son ancienne hauteur mesurée.
 *
 * ## Ce qui ne s'affiche pas, et pourquoi
 *
 * **Le HTML brut qui défile.** Illisible, anxiogène, et un aperçu qui se construirait au fil de
 * l'eau est de toute façon impossible : un fichier sans son `</script>` rend une iframe cassée.
 * On montre donc une **jauge en octets**, le dernier commentaire du modèle, et un pli « voir le
 * code » pour qui veut vérifier. L'aperçu réel n'apparaît qu'à l'évènement `version`, quand le
 * fichier est complet et enregistré.
 *
 * ## ⚠️ Pas de suggestions de demande, et c'est une décision
 *
 * Cet écran a porté trois amorces cliquables — « l'usage de l'application par les agents », « les
 * recherches effectuées dans l'outil », « l'entonnoir jusqu'à l'action finale ». Elles étaient
 * calquées sur le premier dashboard du produit, une application métier interne, et **généralisées
 * depuis un seul exemple**. Sur une boutique, « les agents » et « l'outil » ne veulent rien dire :
 * le cas a été vu, l'amorce cliquée, et le tableau produit était hors sujet.
 *
 * Trois suggestions qui nomment le mauvais métier sont **pires qu'une page blanche** : elles
 * donnent l'impression que le produit devine, et elles ancrent l'auteur sur un modèle mental qui
 * n'est pas le sien. L'auteur connaît son compte ; nous, non.
 *
 * Le rempart contre la demande vague n'est de toute façon pas ici : la consigne du modèle lui
 * impose d'**explorer les données avant d'écrire** (`promptSysteme.js`, bloc A). Une demande
 * imprécise est corrigée par l'exploration, pas par une suggestion.
 *
 * ⚠️ **Si l'envie revient d'en reproposer, qu'elles soient déduites du compte** — pour Matomo,
 * les modules que l'instance déclare (`Ecommerce`, `Events`, `Actions`) le disent —, jamais
 * recopiées d'un dashboard existant. Et rien n'existe d'équivalent pour GA4 ni Piano.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, Send, Loader2, Sparkles, FileCode2, Eye, Check, GitBranch,
  AlertTriangle, ChevronRight, Wrench, Brain, Square
} from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { lireFluxSse, estUnAbandon } from '../../utils/lireFluxSse';
import DashboardSurMesureView from './DashboardSurMesureView';
import { useBulleAuSurvol } from '../BulleSurvol';
import { CONNECTEURS } from '../../constants/connecteurs';
import { SOURCES_AVEC_DASHBOARD } from '../ChoixDashboardAnalytics';
import './VueGenerationDashboard.css';

const API_URL = config.API_URL;

/**
 * Le logo et le nom lisible d'un type de compte, depuis le catalogue des connecteurs.
 *
 * ⚠️ **Dérivé, jamais recopié.** `CONNECTEURS` porte déjà `{ cle, libelle, logo }` pour chaque
 * type : en refaire une table ici garantirait qu'un connecteur ajouté n'y apparaisse jamais, et
 * le symptôme serait une carte sans logo sous un nom en majuscules à rallonge.
 */
const CONNECTEUR_PAR_TYPE = Object.fromEntries(CONNECTEURS.map(c => [c.cle, c]));

const octets = (n) => (n >= 1024 ? `${(n / 1024).toFixed(1)} Ko` : `${n} o`);
const dollars = (n) => `${(Number(n) || 0).toFixed(n < 0.01 ? 4 : 2)} $`;

/*
 * ⚠️ **`compte` peut arriver sans `id`, et c'est le cas courant.**
 *
 * On atteint cet écran depuis le choix des dashboards, qui ne connaît pas toujours le compte : on
 * y vient de l'accueil avec un compte précis, mais aussi de la ligne groupée d'une solution de
 * mesure ou de l'onglet Dashboard, où il n'y a que le *type*. La première version masquait la
 * carte dans ce cas — ce qui rendait la fonctionnalité introuvable, constaté sur l'écran Matomo.
 *
 * La vue demande donc le compte quand elle ne l'a pas. C'est aussi ce qui la rend insensible au
 * défaut voisin : l'écran de choix ne porte pas le compte dans son URL, et le perdait à la
 * moindre resynchronisation.
 */
const VueGenerationDashboard = ({
  token,
  compte,
  /**
   * Le client sélectionné dans l'en-tête, ou `null` pour « tous les clients ».
   *
   * ⚠️ **C'est un filtre d'affichage, pas une borne de sécurité.** Le serveur borne déjà au
   * périmètre de l'utilisateur ; celui-ci dit seulement ce qu'il a choisi de regarder. Les deux
   * s'appliquent en intersection, et jamais l'un à la place de l'autre.
   */
  selectedClientId = null,
  /**
   * Les segments parcourus avant d'arriver ici — « BZB », « Dashboard », « Microsoft Ads ».
   *
   * ⚠️ **Fournis par l'appelant, jamais reconstitués.** Cet écran ne sait pas d'où l'on vient :
   * on y entre depuis le fil d'ariane d'un dashboard, depuis Comptes, ou depuis l'écran de choix.
   * Les deviner reviendrait à inventer un chemin qui n'a pas été pris.
   */
  chemin = [],
  selectedPeriod,
  customStartDate,
  customEndDate,
  onRetour,
  onFicheCreee
}) => {
  /** Le compte retenu : celui qu'on nous a donné, ou celui qu'on choisit ici. */
  const [compteChoisi, setCompteChoisi] = useState(compte?.id ? compte : null);
  const [comptesPossibles, setComptesPossibles] = useState(null);

  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [demande, setDemande] = useState('');
  const [fil, setFil] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [phase, setPhase] = useState('');
  const [octetsEcrits, setOctetsEcrits] = useState(0);
  const [dashboardId, setDashboardId] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState(null);

  /*
   * ⚠️ Le nom du compte vient du **serveur**, pas de la prop, dès que `/generation/etat` a
   * répondu. La prop n'est qu'un libellé de passage : l'écran de choix des dashboards n'a pas le
   * nom du compte — l'accueil appelle `ouvrirChoixDashboard(type, siteId)` sans le transmettre —,
   * si bien que l'en-tête annonçait « ce compte ». Le serveur, lui, a résolu le `LibraryItem`
   * pour borner ses lectures : il connaît son nom, et c'est la seule source qui ne puisse pas
   * se tromper.
   */
  const nomDuCompte = etat?.compte?.nom || compteChoisi?.nom || compteChoisi?.name || '';

  /**
   * Le fil d'ariane, commun aux deux écrans de cette vue.
   *
   * Le dernier segment est l'écran courant : « Générer un dashboard sur mesure » tant qu'aucun
   * compte n'est choisi, le nom du compte ensuite.
   */
  const filDAriane = (extra = null) => (
    <div className="app-breadcrumb">
      <span className="breadcrumb-nav-item" onClick={onRetour} style={{ cursor: 'pointer' }}>
        <ArrowLeft size={14} /> Retour
      </span>
      {chemin.map((segment) => (
        <React.Fragment key={segment}>
          <ChevronRight size={14} className="breadcrumb-separator" />
          <span className="breadcrumb-nav-item">{segment}</span>
        </React.Fragment>
      ))}
      <ChevronRight size={14} className="breadcrumb-separator" />
      <span className={`breadcrumb-nav-item${nomDuCompte ? '' : ' active'}`}>
        <Sparkles size={14} /> Générer un dashboard sur mesure
      </span>
      {nomDuCompte && (
        <>
          <ChevronRight size={14} className="breadcrumb-separator" />
          <span className="breadcrumb-nav-item active">{nomDuCompte}</span>
        </>
      )}
      {extra}
    </div>
  );

  const abandonRef = useRef(null);
  const basRef = useRef(null);
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  /* --------------------------------------------------------------------------------------- *
   * L'état : l'interrupteur, le plafond, et où en est la conversation
   * --------------------------------------------------------------------------------------- */

  const chargerLEtat = useCallback(async () => {
    if (!compteChoisi?.id) { setChargement(false); return; }
    try {
      const { data } = await axios.get(
        `${API_URL}/api/dashboards-sur-mesure/generation/etat`,
        { headers: { Authorization: `Bearer ${token}` }, params: { libraryItemId: compteChoisi.id } }
      );
      setEtat(data);
      // La fiche a pu naître à un tour précédent : c'est la conversation qui s'en souvient.
      if (data.conversation?.dashboardId) setDashboardId(data.conversation.dashboardId);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally {
      setChargement(false);
    }
  }, [token, compteChoisi?.id]);

  useEffect(() => { chargerLEtat(); }, [chargerLEtat]);

  /*
   * Les comptes possibles, quand on n'en a pas. Bornés au type si l'appelant en connaît un —
   * venant de l'écran Matomo, proposer les comptes GA4 serait une question de plus à trancher.
   */
  useEffect(() => {
    if (compteChoisi?.id) return;
    let vivant = true;
    axios.get(`${API_URL}/api/dashboards-sur-mesure/generation/comptes`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        ...(compte?.type ? { type: compte.type } : {}),
        ...(selectedClientId ? { client_id: selectedClientId } : {})
      }
    })
      .then(({ data }) => {
        /*
         * ⚠️ **On n'offre que ce qui s'ouvre.** La route rend tous les comptes dont la case
         * « son dashboard » est cochée ; mais héberger un dashboard sur un type que l'écran de
         * choix ne sait pas afficher, c'est créer quelque chose que rien ne mènerait à ouvrir —
         * le défaut que ce dossier a déjà connu, et dont le code porte encore l'avertissement.
         *
         * Ouvrir un type de plus se fait donc dans `SOLUTIONS`, côté écran de choix, et la liste
         * d'ici suit toute seule.
         */
        if (vivant) {
          setComptesPossibles((data.comptes || []).filter(c => SOURCES_AVEC_DASHBOARD.includes(c.type)));
        }
      })
      .catch((e) => { if (vivant) { setErreur(e.response?.data?.error || e.message); setComptesPossibles([]); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [token, compteChoisi?.id, compte?.type, selectedClientId]);

  // Le fil suit l'écriture, sans l'arracher à qui remonte lire un tour précédent.
  useEffect(() => {
    if (enCours) basRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [fil, enCours, octetsEcrits]);

  /* --------------------------------------------------------------------------------------- *
   * Un tour
   * --------------------------------------------------------------------------------------- */

  const ajouter = (entree) => setFil(prev => [...prev, { cle: `${Date.now()}-${prev.length}`, ...entree }]);

  const lancer = async (texteDemande) => {
    const question = (texteDemande || demande).trim();
    if (!question || enCours) return;

    setDemande('');
    setErreur(null);
    setOctetsEcrits(0);
    setEnCours(true);
    setPhase('Envoi de la demande…');
    ajouter({ type: 'demande', texte: question });

    const controleur = new AbortController();
    abandonRef.current = controleur;

    /*
     * ⚠️ Le texte et la réflexion se cumulent **hors** du gestionnaire : `setFil` est asynchrone,
     * et un morceau de flux arrive toutes les quelques dizaines de millisecondes. Relire l'état
     * React à chaque morceau pour y concaténer le suivant perdrait des caractères.
     */
    let texte = '';
    let reflexion = '';

    const surEvenement = (nom, d) => {
      switch (nom) {
        case 'start':
          setPhase('Exploration des données…');
          if (d.dashboardId) setDashboardId(d.dashboardId);
          if (d.historiqueCoupe) {
            ajouter({ type: 'note', texte: `Les ${d.historiqueCoupe} premiers tours ont été `
              + 'retirés du contexte : la conversation devenait trop longue.' });
          }
          break;

        case 'reflexion':
          reflexion += d.text;
          // Une seule entrée de réflexion par tour, qu'on allonge : une par morceau de flux
          // ferait défiler trois cents plis de deux mots.
          setFil(prev => (prev.some(e => e.type === 'reflexion' && e.encours)
            ? prev.map(e => (e.type === 'reflexion' && e.encours ? { ...e, texte: reflexion } : e))
            : [...prev, { cle: `r-${Date.now()}`, type: 'reflexion', texte: reflexion, encours: true }]));
          break;

        case 'chunk':
          texte += d.text;
          setPhase('');
          setFil(prev => {
            const dernier = prev[prev.length - 1];
            if (dernier?.type === 'texte' && dernier.encours) {
              return [...prev.slice(0, -1), { ...dernier, texte }];
            }
            // Le premier mot visible clôt la réflexion : elle se replie d'elle-même.
            return [
              ...prev.map(e => (e.type === 'reflexion' ? { ...e, encours: false } : e)),
              { cle: `t-${Date.now()}`, type: 'texte', texte, encours: true }
            ];
          });
          break;

        case 'outil':
          if (d.etat === 'debut') {
            ajouter({ type: 'outil', nom: d.nom, encours: true });
            setPhase(LIBELLES_OUTILS[d.nom] || d.nom);
          } else {
            setFil(prev => {
              const i = [...prev].reverse().findIndex(e => e.type === 'outil' && e.nom === d.nom && e.encours);
              if (i < 0) return prev;
              const index = prev.length - 1 - i;
              const copie = [...prev];
              copie[index] = { ...copie[index], encours: false, resume: d.resume, refus: d.etat === 'refus' };
              return copie;
            });
          }
          break;

        case 'fiche':
          setDashboardId(d.dashboardId);
          ajouter({ type: 'fiche', dashboardId: d.dashboardId, epreuves: d.epreuves || [] });
          if (onFicheCreee) onFicheCreee(d.dashboardId);
          break;

        case 'html':
          setOctetsEcrits(d.octets);
          setPhase('Écriture du fichier…');
          break;

        case 'version':
          /*
           * Un candidat. ⚠️ Il est **enregistré sans être promu** : la fiche garde sa version
           * publiée, et c'est ce qui permet de le prévisualiser avec de vraies données sans que
           * rien ne change pour le client.
           */
          ajouter({
            type: 'version',
            versionId: d.versionId,
            dashboardId: d.dashboardId,
            octets: d.octets,
            inchangee: d.inchangee,
            contrat: d.contrat,
            a: new Date().toISOString()
          });
          setApercu({ dashboardId: d.dashboardId, versionId: d.versionId });
          break;

        case 'cout':
          // Dit **pendant**, pas après : c'est ce qui permet d'arrêter un tour qui dérape.
          setFil(prev => [...prev, {
            cle: `c-${Date.now()}-${d.tour}`, type: 'cout', tour: d.tour, usage: d
          }]);
          break;

        case 'error':
          // Posé dans le fil, **sans vider ce qui précède** : la réflexion et le texte déjà
          // écrits ont été payés, et ils expliquent souvent l'erreur.
          ajouter({ type: 'erreur', texte: d.message });
          break;

        case 'done':
          setEtat(prev => (prev ? { ...prev, consommation: d.consommation || prev.consommation } : prev));
          ajouter({ type: 'bilan', usage: d.usage, tours: d.tours, dureeMs: d.dureeMs, tronque: d.tronque });
          break;

        default:
          break;
      }
    };

    try {
      await lireFluxSse(`${API_URL}/api/dashboards-sur-mesure/generation/stream`, {
        token,
        corps: { libraryItemId: compteChoisi.id, dashboardId, demande: question },
        signal: controleur.signal,
        surEvenement,
        refusParDefaut: 'Le serveur a refusé la génération'
      });
    } catch (e) {
      if (!estUnAbandon(e)) {
        console.error('[GENERATION]', e);
        setErreur(e.message);
      }
    } finally {
      setEnCours(false);
      setPhase('');
      abandonRef.current = null;
      setFil(prev => prev.map(e => ({ ...e, encours: false })));
      chargerLEtat();
    }
  };

  const arreter = () => {
    abandonRef.current?.abort();
    ajouter({ type: 'note', texte: 'Génération interrompue. Ce qui avait été écrit est conservé.' });
  };

  /* --------------------------------------------------------------------------------------- *
   * Garder une version
   * --------------------------------------------------------------------------------------- */

  /**
   * Garder une version.
   *
   * ⚠️ **Deux écritures, et la seconde est celle qui manquait.** Promouvoir la version ne suffit
   * pas : une fiche naît `actif: false`, et l'écran de choix des dashboards filtre précisément
   * là-dessus (`if (!d.actif) return false`). On gardait donc une version que **rien n'affichait
   * nulle part** — le dashboard existait en base, invisible, et une note dans le fil disait
   * d'aller cocher une case ailleurs. Personne ne le fait : une note défile, et on vient de
   * cliquer sur « Garder » en croyant avoir fini.
   *
   * ⚠️ **`visibleClient` n'est pas touché**, et c'est délibéré : montrer un tableau à un client
   * est une décision qui lui appartient, pas la conséquence d'un clic sur « Garder ». Elle se
   * prend dans la modale du compte, en connaissance de cause.
   */
  const garder = async (entree) => {
    const entetes = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.put(
        `${API_URL}/api/dashboards-sur-mesure/${entree.dashboardId}/version-actuelle`,
        { versionId: entree.versionId },
        entetes
      );
      await axios.put(
        `${API_URL}/api/dashboards-sur-mesure/${entree.dashboardId}`,
        { actif: true },
        entetes
      );
      ajouter({
        type: 'note',
        texte: 'Gardé. Le dashboard est publié et apparaît maintenant dans l\'écran de choix du '
          + 'compte. Il reste invisible de vos clients tant que vous ne l\'avez pas décidé, dans '
          + 'la modale du compte.',
        lien: { href: `#dashboard-sur-mesure?dashboard=${entree.dashboardId}`, libelle: 'L\'ouvrir' }
      });
      setFil(prev => prev.map(e => (
        e.type === 'version' ? { ...e, promue: e.versionId === entree.versionId } : e
      )));
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    }
  };

  const repartirDe = (entree) => {
    setApercu({ dashboardId: entree.dashboardId, versionId: entree.versionId });
    setDemande('Repars de cette version et ');
  };

  /* --------------------------------------------------------------------------------------- *
   * Le rendu
   * --------------------------------------------------------------------------------------- */

  if (chargement) {
    return (
      <div className="kgen-attente">
        <Loader2 className="kgen-tourne" size={22} />
        <span>Lecture de l'état…</span>
      </div>
    );
  }

  /*
   * Pas de compte : on le demande. ⚠️ C'est un écran, pas un refus — le refus était le défaut
   * d'origine, qui faisait simplement disparaître la porte d'entrée.
   */
  if (!compteChoisi?.id) {
    return (
      /*
       * ⚠️ `kgen-libre` : **pas de hauteur imposée ici.** La hauteur fixe de `.kgen` existe pour
       * que la conversation et l'aperçu défilent chacun de son côté ; sur une simple liste de
       * comptes, elle fabrique un **second ascenseur** à l'intérieur de la page, qui en a déjà
       * un. Deux ascenseurs imbriqués, c'est la molette qui ne sait plus lequel elle déplace.
       */
      <div className="kgen kgen-libre">
        {/*
          Le fil d'ariane de l'application, et non un en-tête propre à cet écran.

          ⚠️ `app-breadcrumb` porte `margin: -30px` et `width: calc(100% + 60px)` : il compte être
          le premier enfant de `.main-content` et déborde volontairement de son padding pour que
          son filet aille d'un bord à l'autre. Le poser ailleurs le décalerait.
        */}
        {filDAriane()}

        <div className="kgen-choix-compte">
          <h2>Sur quel compte&nbsp;?</h2>
          <p>
            Claude lira les données de ce compte pour écrire le tableau de bord. Le compte borne
            tout ce qu'il pourra demander, pendant l'écriture comme après.
          </p>

          {erreur && <div className="kgen-erreur">{erreur}</div>}

          {comptesPossibles === null && (
            <div className="kgen-encours"><Loader2 className="kgen-tourne" size={14} /> Lecture…</div>
          )}

          {comptesPossibles?.length === 0 && (
            <p className="kgen-vide">
              Aucun compte disponible. Un compte doit être actif et avoir sa case
              «&nbsp;son dashboard&nbsp;» cochée, sur sa fiche dans Comptes.
            </p>
          )}

          <div className="kgen-comptes">
            {(comptesPossibles || []).map((c) => (
              <button
                key={c.id}
                type="button"
                className="kgen-compte-carte"
                onClick={() => { setChargement(true); setErreur(null); setCompteChoisi(c); }}
              >
                {CONNECTEUR_PAR_TYPE[c.type]?.logo && (
                  <img
                    src={CONNECTEUR_PAR_TYPE[c.type].logo}
                    alt=""
                    className="kgen-compte-logo"
                  />
                )}
                <span className="kgen-compte-texte">
                  <span className="kgen-compte-nom">{c.nom}</span>
                  {/* Le libellé du catalogue, pas la clé : « Meta Ads » et non `FACEBOOK_ADS`. */}
                  <span className="kgen-compte-type">
                    {CONNECTEUR_PAR_TYPE[c.type]?.libelle || c.type}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const conso = etat?.consommation;
  const ferme = etat && !etat.ouverte;

  return (
    <div className="kgen">
      {/* Le même fil, avec le compte et la dépense du mois en plus. Voir `filDAriane`. */}
      {filDAriane(conso && (
        <div
          /*
            * ⚠️ La couleur **dit quelque chose** : elle suit la part du plafond consommée, et
            * c'est la seule information que ce badge puisse porter sans texte supplémentaire.
            * Un badge d'une couleur fixe ne serait qu'une décoration posée sur un chiffre que
            * personne ne relie à sa limite.
            */
          className={`kgen-plafond ksi-survolable ${
            conso.dollars >= conso.plafondDollars * 0.9 ? 'critique'
              : conso.dollars >= conso.plafondDollars * 0.75 ? 'proche' : ''
          }`}
          onMouseEnter={survoler(
            `${conso.generations} génération(s) ce mois-ci sur ${conso.plafondGenerations}.\n`
            + `Dépense : ${dollars(conso.dollars)} sur ${dollars(conso.plafondDollars)}.\n\n`
            + 'Le plafond en dollars est le seul qui borne vraiment la facture : il ne dépend '
            + 'd\'aucune estimation par génération.'
          )}
          onMouseLeave={quitter}
        >
          {dollars(conso.dollars)} ce mois-ci
        </div>
      ))}

      {ferme && (
        <div className="kgen-bandeau">
          <AlertTriangle size={15} />
          <span>
            La génération est fermée. Elle s'ouvre depuis Administration, et seulement par
            l'éditeur de Kokpit — la facture est chez lui.
          </span>
        </div>
      )}

      <div className="kgen-corps">
        <div className="kgen-colonne">
          <div className="kgen-fil">
            {fil.map((e) => (
              <EntreeDuFil
                key={e.cle}
                entree={e}
                survoler={survoler}
                quitter={quitter}
                onPrevisualiser={() => setApercu({ dashboardId: e.dashboardId, versionId: e.versionId })}
                onGarder={() => garder(e)}
                onRepartir={() => repartirDe(e)}
                actif={apercu?.versionId === e.versionId}
              />
            ))}

            {enCours && (
              <div className="kgen-encours">
                <Loader2 className="kgen-tourne" size={14} />
                <span>{phase || 'Réflexion…'}</span>
                {octetsEcrits > 0 && (
                  <span className="kgen-jauge" title={`${octetsEcrits} octets écrits`}>
                    <FileCode2 size={12} /> {octets(octetsEcrits)}
                  </span>
                )}
              </div>
            )}

            <div ref={basRef} />
          </div>

          {erreur && <div className="kgen-erreur">{erreur}</div>}

          <div className="kgen-saisie">
            <textarea
              value={demande}
              onChange={(ev) => setDemande(ev.target.value)}
              onKeyDown={(ev) => {
                // Entrée envoie, Maj+Entrée va à la ligne : une demande fait souvent trois lignes.
                if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); lancer(); }
              }}
              placeholder={fil.length
                ? 'Une retouche : « agrandis le graphique », « ajoute une colonne »…'
                : 'Ce que le tableau doit montrer…'}
              rows={3}
              disabled={enCours || ferme}
            />
            <div className="kgen-saisie-pied">
              <span className="kgen-indice">
                {enCours
                  ? 'Une retouche coûte vingt fois moins qu\'une réécriture : demandez un changement à la fois.'
                  : `Entrée pour envoyer · ${etat?.modele || ''}`}
              </span>
              {enCours ? (
                <button type="button" className="kgen-arreter" onClick={arreter}>
                  <Square size={13} /> Arrêter
                </button>
              ) : (
                <button
                  type="button"
                  className="kgen-envoyer"
                  disabled={!demande.trim() || ferme}
                  onClick={() => lancer()}
                >
                  <Send size={14} /> Envoyer
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="kgen-apercu">
          {apercu ? (
            /*
             * ⚠️ La `key` force le **remontage** à chaque changement de version. `versionId` est
             * bien dans les dépendances de l'hôte, mais une iframe dont on remplace le `srcdoc`
             * à chaud conserve la hauteur qu'elle avait mesurée : la version suivante s'affiche
             * dans le cadre de la précédente, tronquée ou flottante.
             */
            <DashboardSurMesureView
              key={`${apercu.dashboardId}:${apercu.versionId}`}
              token={token}
              dashboardId={apercu.dashboardId}
              versionId={apercu.versionId}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
            />
          ) : (
            <div className="kgen-apercu-vide">
              <FileCode2 size={28} />
              <p>L'aperçu apparaîtra ici dès qu'une version sera écrite.</p>
              <p className="kgen-apercu-note">
                Il lit les <strong>vraies données</strong> du compte sur la période choisie :
                c'est la seule façon de juger une écriture avant de la publier.
              </p>
            </div>
          )}
        </div>
      </div>
      {bulle}
    </div>
  );
};

/** Ce que chaque outil est en train de faire, dit en français. */
const LIBELLES_OUTILS = {
  lister_les_champs: 'Lecture du catalogue de la source…',
  lire_des_donnees: 'Lecture d\'un échantillon de données…',
  proposer_fiche: 'Déclaration des requêtes, et épreuve de chacune…',
  lire_le_html: 'Relecture du fichier…',
  remplacer_dans_le_html: 'Retouche du fichier…'
};

/** Une entrée du fil. Séparée pour que chaque forme reste lisible. */
const EntreeDuFil = ({ entree, survoler, quitter, onPrevisualiser, onGarder, onRepartir, actif }) => {
  const [deplie, setDeplie] = useState(false);

  switch (entree.type) {
    case 'demande':
      return <div className="kgen-demande">{entree.texte}</div>;

    case 'reflexion':
      return (
        <div className={`kgen-reflexion${entree.encours ? ' encours' : ''}`}>
          <button type="button" className="kgen-pli" onClick={() => setDeplie(!deplie)}>
            <Brain size={13} />
            <span>Réflexion</span>
            <ChevronRight size={13} className={deplie ? 'ouvert' : ''} />
          </button>
          {deplie && <div className="kgen-reflexion-texte">{entree.texte}</div>}
        </div>
      );

    case 'texte':
      return <div className="kgen-texte">{entree.texte}</div>;

    case 'outil':
      return (
        <div className={`kgen-outil${entree.refus ? ' refus' : ''}`}>
          {entree.encours ? <Loader2 className="kgen-tourne" size={12} /> : <Wrench size={12} />}
          <span className="kgen-outil-nom">{LIBELLES_OUTILS[entree.nom] || entree.nom}</span>
          {entree.resume && <span className="kgen-outil-resume">{entree.resume}</span>}
        </div>
      );

    case 'fiche':
      return (
        <div className="kgen-fiche">
          <Check size={13} />
          <div>
            <strong>Requêtes déclarées et éprouvées</strong>
            {/*
              Les épreuves valent plus qu'un « validé » : `validerFiche` vérifie les **noms**
              contre le catalogue, pas que le croisement existe — Matomo refuse des couples que
              les deux noms autorisent séparément. C'est la lecture d'épreuve qui le dit.
            */}
            <ul className="kgen-epreuves">
              {entree.epreuves.map((ep) => (
                <li key={ep.cle} className={ep.ok ? 'ok' : 'echec'}>
                  <code>{ep.cle}</code>
                  <span>{ep.ok ? `${ep.lignes} ligne(s)` : ep.erreur}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );

    case 'version': {
      const contrat = entree.contrat || {};
      const aDire = (contrat.refus?.length || 0) + (contrat.avertissements?.length || 0);
      return (
        <div className={`kgen-version${actif ? ' actif' : ''}${entree.promue ? ' promue' : ''}`}>
          <div className="kgen-version-entete">
            <FileCode2 size={14} />
            <strong>{entree.inchangee ? 'Fichier inchangé' : 'Nouvelle version'}</strong>
            <span className="kgen-version-octets">{octets(entree.octets)}</span>
            {entree.promue && <span className="kgen-badge">publiée</span>}
          </div>

          {aDire > 0 && (
            <div className={`kgen-contrat${contrat.refus?.length ? ' refus' : ''}`}>
              <AlertTriangle size={12} />
              <span>
                {contrat.refus?.length
                  ? `${contrat.refus.length} point(s) à corriger`
                  : `${contrat.avertissements.length} point(s) à vérifier`}
              </span>
              <ul>
                {[...(contrat.refus || []), ...(contrat.avertissements || [])].map((r, i) => (
                  <li key={`${r.regle}-${i}`}>{r.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="kgen-version-actions">
            <button type="button" onClick={onPrevisualiser}>
              <Eye size={13} /> Prévisualiser
            </button>
            <button type="button" onClick={onRepartir}>
              <GitBranch size={13} /> Repartir de celle-ci
            </button>
            {/*
              « Garder » est à droite : la fin de ligne est la position de validation, et c'est
              là que le regard termine sa lecture. Même parti que les cartes de Radar.
            */}
            <button
              type="button"
              className="kgen-garder"
              disabled={entree.promue || contrat.refus?.length > 0}
              onMouseEnter={contrat.refus?.length
                ? survoler('Un document qui enfreint le contrat ne peut pas être publié : le bac '
                  + 'à sable refuserait en silence ce qu\'il interdit, et le dashboard '
                  + 's\'afficherait à moitié chez le client.')
                : undefined}
              onMouseLeave={quitter}
              onClick={onGarder}
            >
              <Check size={13} /> Garder
            </button>
          </div>
        </div>
      );
    }

    case 'cout':
      return (
        <div
          className="kgen-cout ksi-survolable"
          onMouseEnter={survoler(
            `Tour ${entree.tour}\n`
            + `Entrée : ${entree.usage.entree} jetons\n`
            + `Sortie : ${entree.usage.sortie} jetons\n`
            + `Cache lu : ${entree.usage.cacheLu} · écrit : ${entree.usage.cacheEcrit}\n\n`
            + 'Une lecture de cache coûte un dixième d\'un jeton d\'entrée : c\'est elle qui rend '
            + 'une retouche vingt fois moins chère qu\'une réécriture.'
          )}
          onMouseLeave={quitter}
        >
          tour {entree.tour} · {dollars(entree.usage.cout)}
        </div>
      );

    case 'bilan':
      return (
        <div className="kgen-bilan">
          {entree.tronque && (
            <div className="kgen-tronque">
              <AlertTriangle size={12} />
              Réponse coupée : rien n'a été enregistré. Demandez une retouche plutôt qu'une
              réécriture, ou découpez la demande.
            </div>
          )}
          <span>
            {entree.tours} appel(s) · {Math.round((entree.dureeMs || 0) / 1000)} s ·{' '}
            {dollars(entree.usage?.cout)}
          </span>
        </div>
      );

    case 'erreur':
      return <div className="kgen-erreur-fil"><AlertTriangle size={13} /> {entree.texte}</div>;

    case 'note':
      return (
        <div className="kgen-note">
          {entree.texte}
          {entree.lien && (
            <> <a className="kgen-note-lien" href={entree.lien.href}>{entree.lien.libelle}</a></>
          )}
        </div>
      );

    default:
      return null;
  }
};

export default VueGenerationDashboard;
